import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Share2 } from 'lucide-react';

// Inline brand glyphs (so logos always render correctly & look premium)
const Icons = {
  whatsapp: (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.157 5.335 5.492 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.601 5.361l-.999 3.648 3.887-1.02zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
  ),
  telegram: (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
  ),
};

export default function ShareSheet({ open, onClose, url, title = '', text = '', modes = null }) {
  const [copied, setCopied] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [modeIdx, setModeIdx] = useState(0);

  useEffect(() => {
    if (open) { setMounted(true); setCopied(false); setModeIdx(0); }
  }, [open]);

  if (!open && !mounted) return null;

  // Optional mode toggle (e.g. Share for Rent / Share for Buy)
  const hasModes = Array.isArray(modes) && modes.length > 1;
  const activeMode = hasModes ? modes[modeIdx] : null;
  const shareUrl = activeMode?.url || url;
  const shareLabel = activeMode?.text ?? text;

  const shareText = [title, shareLabel].filter(Boolean).join(' — ');
  const enc = encodeURIComponent;

  const targets = [
    { key: 'whatsapp', label: 'WhatsApp', color: '#25D366', href: `https://wa.me/?text=${enc(`${shareText}\n${shareUrl}`)}` },
    { key: 'facebook', label: 'Facebook', color: '#1877F2', href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}` },
    { key: 'x', label: 'X', color: '#000000', href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}` },
    { key: 'telegram', label: 'Telegram', color: '#229ED9', href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copy this link:', shareUrl);
    }
  };

  const native = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text: shareText, url: shareUrl }); onClose?.(); } catch { /* cancelled */ }
    } else {
      copy();
    }
  };

  const close = () => { setMounted(false); setTimeout(() => onClose?.(), 200); };

  return (
    <div
      onClick={close}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(7,20,42,0.55)', backdropFilter: 'blur(4px)', opacity: open && mounted ? 1 : 0, transition: 'opacity .2s ease' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6"
        style={{
          transform: open && mounted ? 'translateY(0) scale(1)' : 'translateY(24px) scale(.98)',
          transition: 'transform .28s cubic-bezier(.16,1,.3,1)',
        }}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold" style={{ color: '#07142A' }}>Share this vehicle</h3>
          <button onClick={close} className="p-2 rounded-full hover:bg-gray-100 text-gray-500"><X size={20} /></button>
        </div>

        {hasModes && (
          <div className="flex p-1 mb-5 rounded-xl bg-gray-100">
            {modes.map((m, i) => (
              <button key={m.key} onClick={() => { setModeIdx(i); setCopied(false); }}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
                style={modeIdx === i
                  ? { background: '#07142A', color: '#fff', boxShadow: '0 2px 8px rgba(7,20,42,.25)' }
                  : { color: '#6b7280' }}>
                {m.label}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-4 gap-3 mb-5">
          {targets.map((t) => (
            <a key={t.key} href={t.href} target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 group">
              <span
                className="w-14 h-14 rounded-full flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform"
                style={{ background: t.color }}
              >{Icons[t.key]}</span>
              <span className="text-xs font-medium text-gray-600">{t.label}</span>
            </a>
          ))}
        </div>

        <button onClick={native}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white mb-3"
          style={{ background: '#07142A' }}>
          <Share2 size={18} /> More options
        </button>

        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl p-2 pl-4">
          <span className="flex-1 truncate text-sm text-gray-500">{shareUrl}</span>
          <button onClick={copy}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold text-sm text-white transition-colors"
            style={{ background: copied ? '#16a34a' : '#FFD400', color: copied ? '#fff' : '#07142A' }}>
            {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy</>}
          </button>
        </div>
      </div>
    </div>
  );
}
